import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import PremiumCalculator from './components/PremiumCalculator';
import ProductNav from '../../components/feature/ProductNav';
import ServingCommunitySection from '../../components/feature/ServingCommunitySection';
import { BookingCalendar } from '../home/components/BookingCalendar';
import { ABTestHero } from './components/ABTestHero';
import { useABTest } from '../../hooks/useABTest';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

export default function TermLifePage() {
  const [showBooking, setShowBooking] = useState(false);
  const { trackConversion } = useABTest('term-life-hero');

  const handleCTAClick = () => {
    trackConversion({ action: 'cta_clicked', page: 'term-life' });
    setShowBooking(true);
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${SITE_URL}/term-life#service`,
    name: 'Term Life Insurance Indianapolis',
    description: 'Affordable term life insurance coverage for Indianapolis families. Get instant quotes and flexible coverage options from 10 to 30 years.',
    provider: {
      '@id': `${SITE_URL}/#organization`
    },
    areaServed: {
      '@type': 'State',
      name: 'Indiana'
    },
    serviceType: 'Term Life Insurance',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '127',
      bestRating: '5',
      worstRating: '1'
    },
    review: [
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Sarah Johnson'
        },
        datePublished: '2024-11-15',
        reviewBody: 'Benjamin helped us get $750K of term life coverage for less than we expected. The process was incredibly smooth - no medical exam required and we were approved in 48 hours. He explained everything clearly and never pressured us. Highly recommend for Indianapolis families.',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        }
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Michael Chen'
        },
        datePublished: '2024-10-22',
        reviewBody: 'As a self-employed business owner in Fishers, I needed affordable life insurance fast. Hoosier Life Insurance compared rates from multiple carriers and found me a 20-year term policy that fit my budget perfectly. Great local service and expertise.',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        }
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Jennifer Martinez'
        },
        datePublished: '2024-09-08',
        reviewBody: 'We were new homeowners in Carmel and wanted to protect our mortgage. Benjamin walked us through term life vs whole life options and helped us choose the right coverage amount. The entire application took 15 minutes online. Excellent experience.',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        }
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'David Thompson'
        },
        datePublished: '2024-08-19',
        reviewBody: 'I compared quotes from 3 different agents and Hoosier Life Insurance beat them all. Got $1M in coverage for $68/month at age 42. Benjamin was patient, knowledgeable, and made the whole process stress-free. This is how insurance should be done.',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        }
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Amanda Wilson'
        },
        datePublished: '2024-07-30',
        reviewBody: 'As a single mom in Indianapolis, I needed affordable coverage to protect my kids. Benjamin found me a $500K policy for just $32/month with no medical exam. The peace of mind is worth every penny. Thank you for making this so easy!',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        }
      }
    ]
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/term-life#webpage`,
    url: `${SITE_URL}/term-life`,
    name: 'Term Life Insurance Indianapolis | Affordable Coverage Quotes',
    description: 'Get affordable term life insurance in Indianapolis. Compare rates, coverage options, and get instant quotes. Protect your family with flexible 10-30 year terms.',
    inLanguage: 'en-US',
    datePublished: '2024-01-15T00:00:00-05:00',
    dateModified: new Date().toISOString(),
    isPartOf: {
      '@id': `${SITE_URL}/#website`
    },
    breadcrumb: {
      '@id': `${SITE_URL}/term-life#breadcrumb`
    },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', '.key-takeaways-heading', '#apply-section-heading']
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${SITE_URL}/term-life#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Term Life Insurance',
        item: `${SITE_URL}/term-life`
      }
    ]
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How much does term life insurance cost in Indiana?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A healthy 35-year-old in Indiana can get $500,000 of 20-year term coverage for $25–$40/month. Rates vary based on age, health, term length, and coverage amount. Indiana residents typically pay 8-12% less than the national average due to the state\'s competitive insurance market and lower cost of living.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much term life insurance do I need?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A common rule is 10–12x your annual income. Your actual needs depend on your mortgage, debts, income replacement needs, and future expenses like college tuition. For example, if you earn $75,000/year with a $200,000 mortgage, consider $950,000-$1,100,000 in coverage.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I convert my term life policy to permanent insurance?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most term life policies include a conversion option allowing you to convert to whole life or universal life without a medical exam, typically within the first 10–20 years. This is valuable if your health declines or you decide you need permanent coverage later.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I get term life insurance without a medical exam in Indiana?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Most Indiana residents qualify for no-exam term life insurance up to $500,000-$1,000,000 through accelerated underwriting. You answer health questions online, and the carrier uses prescription databases and medical records to approve you within 24-48 hours. Higher coverage amounts or certain health conditions may require a traditional exam.',
        },
      },
      {
        '@type': 'Question',
        name: 'What term length should I choose: 10, 20, or 30 years?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Choose a term length that matches your financial obligations. 20-year terms are most popular for young families with mortgages and children. 30-year terms work well if you have young children and want coverage until they finish college. 10-year terms are ideal for short-term debts or supplementing existing coverage. You can always convert to permanent insurance later if needed.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I choose a beneficiary for my term life insurance?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Your beneficiary receives the death benefit tax-free. You can name one or multiple beneficiaries (spouse, children, trust, charity, etc.) and specify what percentage each receives. Always name contingent beneficiaries in case your primary beneficiary passes away first. You can change beneficiaries anytime by contacting your insurance company. Avoid naming minor children directly—use a trust or custodian instead.',
        },
      },
      {
        '@type': 'Question',
        name: 'What happens if I outlive my term life insurance policy?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'If you outlive your term, the policy expires and no death benefit is paid. You have several options: convert to permanent insurance before the term ends (no medical exam required), purchase a new term policy (requires new underwriting at your current age), or let it lapse if you no longer need coverage. Many people find they don\'t need life insurance after retirement when their mortgage is paid off and children are financially independent.',
        },
      },
      {
        '@type': 'Question',
        name: 'Does Indiana have any special protections for life insurance policyholders?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Indiana offers strong consumer protections: the Indiana Life & Health Guaranty Association protects policies up to $300,000 if your carrier becomes insolvent (one of the highest limits in the nation), a minimum 10-day free look period (most carriers offer 30 days), no state income tax on death benefits, and no state estate tax. The Indiana Department of Insurance regulates all carriers and handles consumer complaints.',
        },
      },
    ],
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    '@id': `${SITE_URL}/term-life#howto`,
    name: 'How to Apply for Term Life Insurance in Indianapolis',
    description: 'Get covered in as little as 24 hours with our streamlined term life insurance application process in Indiana.',
    totalTime: 'PT1H',
    estimatedCost: {
      '@type': 'MonetaryAmount',
      currency: 'USD',
      value: '0'
    },
    supply: [
      {
        '@type': 'HowToSupply',
        name: 'Basic health information (height, weight, medications)'
      },
      {
        '@type': 'HowToSupply',
        name: 'Beneficiary details'
      },
      {
        '@type': 'HowToSupply',
        name: 'Driver\'s license or state ID'
      }
    ],
    tool: [
      {
        '@type': 'HowToTool',
        name: 'Phone or computer for online application'
      }
    ],
    step: [
      {
        '@type': 'HowToStep',
        position: '1',
        name: 'Get Your Free Quote — 5 Minutes',
        text: 'Answer a few simple questions about your age, health, and coverage needs. We instantly compare rates from 20+ top-rated carriers including Mutual of Omaha, Prudential, and Nationwide. No personal info required, no obligation.',
        url: `${SITE_URL}/term-life#apply-step-1`
      },
      {
        '@type': 'HowToStep',
        position: '2',
        name: 'Complete Your Application — 15 Minutes',
        text: 'Complete a simple online application or schedule a call with a licensed Indiana agent. Most applicants qualify for no medical exam coverage. You need basic health information, beneficiary details, and a driver\'s license or state ID.',
        url: `${SITE_URL}/term-life#apply-step-2`
      },
      {
        '@type': 'HowToStep',
        position: '3',
        name: 'Get Approved and Activate Coverage',
        text: 'Most Indiana residents receive approval within 24-48 hours. Coverage begins immediately after your first payment. Policy documents arrive by email and mail. All policies include a 30-Day Money-Back Guarantee.',
        url: `${SITE_URL}/term-life#apply-step-3`
      }
    ]
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Set page title — under 60 chars
    document.title = 'Term Life Insurance Indianapolis | Hoosier Life';

    // Helper function to set or create meta tags
    const setOrCreate = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Set meta tags
    setOrCreate('description', 'Affordable term life insurance in Indianapolis, IN. 10, 20, or 30-year terms from $25/mo. Compare 6+ A-rated carriers. Free quote — call (317) 648-2983.');
    setOrCreate('keywords', 'term life insurance Indiana, affordable life insurance, term life quotes, Indiana life insurance rates');
    setOrCreate('last-modified', new Date().toISOString());
    
    // Open Graph tags — use working readdy.ai image URL
    setOrCreate('og:title', 'Term Life Insurance Indianapolis | Hoosier Life');
    setOrCreate('og:description', 'Affordable term life insurance in Indianapolis, IN. 10, 20, or 30-year terms from $25/mo. Compare 6+ A-rated carriers. Free quote — call (317) 648-2983.');
    setOrCreate('og:url', 'https://hoosierlifeinsurance.com/term-life');
    setOrCreate('og:type', 'website');
    setOrCreate('og:site_name', 'Hoosier Life Insurance');
    setOrCreate('og:image', 'https://public.readdy.ai/ai/img_res/1e9c6d0dcf35e1e505488e551035941f.jpg');
    setOrCreate('og:image:width', '1200');
    setOrCreate('og:image:height', '630');
    
    // Twitter Card tags
    setOrCreate('twitter:card', 'summary_large_image');
    setOrCreate('twitter:title', 'Term Life Insurance Indianapolis | Hoosier Life');
    setOrCreate('twitter:description', 'Affordable term life insurance in Indianapolis, IN. 10, 20, or 30-year terms from $25/mo. Compare 6+ A-rated carriers.');
    setOrCreate('twitter:image', 'https://readdy.ai/api/search-image?query=professional%20life%20insurance%20agent%20sitting%20with%20an%20Indianapolis%20family%20reviewing%20term%20life%20insurance%20documents%20at%20a%20kitchen%20table%20warm%20natural%20lighting%20trustworthy%20financial%20planning%20atmosphere%20modern%20home%20clean%20background%20photorealistic%20professional%20photography&width=1200&height=630&seq=og-term-life-v1&orientation=landscape');

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${SITE_URL}/term-life`);

    // InsuranceProduct Schema — for AI + structured data enrichment
    const insuranceProductSchema = {
      '@context': 'https://schema.org',
      '@type': 'InsuranceProduct',
      '@id': `${SITE_URL}/term-life#insurance-product`,
      name: 'Term Life Insurance',
      description: 'Affordable term life insurance covering 10, 20, or 30-year periods with level premiums. Coverage from $100,000 to $5,000,000. Available to Indiana individuals, families, and seniors from 20+ A-rated carriers with approvals in as little as 24–48 hours.',
      url: `${SITE_URL}/term-life`,
      category: 'Life Insurance',
      brand: { '@type': 'Brand', name: 'Hoosier Life Insurance' },
      provider: { '@type': 'Organization', '@id': `${SITE_URL}/#organization` },
      isRelatedTo: { '@type': 'Organization', '@id': `${SITE_URL}/#business` },
      areaServed: { '@type': 'State', name: 'Indiana' },
      offers: {
        '@type': 'Offer',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          priceCurrency: 'USD',
          description: 'Starting from $15/month for healthy adults under 40',
        },
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/term-life`,
      },
    };

    const schemaScripts: HTMLScriptElement[] = [];
    [webPageSchema, serviceSchema, breadcrumbSchema, faqSchema, howToSchema, insuranceProductSchema].forEach((schema, i) => {
      const script = document.createElement('script');
      script.id = `schema-term-life-${i}`;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      schemaScripts.push(script);
    });

    return () => {
      schemaScripts.forEach(s => { try { document.head.removeChild(s); } catch (e) { /* ignore */ } });
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />

      {/* Breadcrumb Navigation */}
      <nav aria-label="Breadcrumb" className="bg-[#1A3A52] border-b border-[#1A3A52]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 text-sm">
          <a href="/" className="flex items-center gap-1.5 text-white/70 hover:text-[#D97642] transition-colors">
            <i className="ri-home-4-line text-base"></i>
            <span>Home</span>
          </a>
          <i className="ri-arrow-right-s-line text-white/40"></i>
          <span className="text-[#D97642] font-semibold">Term Life Insurance</span>
        </div>
      </nav>

      {/* Product Navigation */}
      <ProductNav />

      {/* Star Rating Display */}
      <StarRatingDisplay rating={4.9} reviewCount={127} productName="Term Life Insurance Indianapolis" />

      {/* A/B Test Hero Section */}
      <ABTestHero onCTAClick={handleCTAClick} />

      {/* Key Takeaways — AI SEO Signal */}
      <section className="py-8 bg-[#1A3A52]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-white font-bold text-lg mb-3">Key Takeaways: Term Life Insurance in Indiana</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'A healthy 35-year-old in Indianapolis can get $500,000 of life insurance for about $28/month — that\'s less than Netflix',
                  'Term life is the most affordable type of life insurance — up to 15x cheaper than whole life for the same coverage',
                  'Most Indiana families get approved in 24–48 hours with no doctor visit, no blood draw, no waiting room',
                  'We shop 20+ A-rated carriers — Mutual of Omaha, Prudential, Nationwide & more — to find your lowest rate',
                  'If your health changes later, you can convert your policy to permanent coverage with no new health exam',
                  'When your family gets paid, it\'s 100% tax-free — the IRS and Indiana both stay out of it completely',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/90 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-white/50 text-xs">Last reviewed March 2025 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* How Term Life Works in Indiana */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="how-term-works-heading" className="text-4xl font-bold text-gray-900 mb-4">
              How Term Life Insurance Works in Indiana
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              It&apos;s actually really simple. You pay a small amount every month. If you die during the policy, your family gets a big tax-free check. If you don&apos;t die, no money changes hands — but your family was protected the whole time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gradient-to-br from-white to-white p-8 rounded-xl border border-gray-100">
              <div className="w-14 h-14 bg-[#D97642] rounded-full flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Choose Your Coverage</h3>
              <p className="text-gray-600 leading-relaxed">
                Select a coverage amount (typically $250K-$1M) and term length (10, 20, or 30 years) based on your family's needs and budget.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-white p-8 rounded-xl border border-gray-100">
              <div className="w-14 h-14 bg-[#1A3A52] rounded-full flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Apply &amp; Get Approved</h3>
              <p className="text-gray-600 leading-relaxed">
                Complete a simple health questionnaire. Most Indiana residents get approved within 24-48 hours with no medical exam required.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-white p-8 rounded-xl border border-gray-100">
              <div className="w-14 h-14 bg-[#D97642] rounded-full flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Pay Fixed Premiums</h3>
              <p className="text-gray-600 leading-relaxed">
                Your monthly premium stays the same for the entire term. Lock in low rates while you're young and healthy.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-white p-8 rounded-xl border border-gray-100">
              <div className="w-14 h-14 bg-[#1A3A52] rounded-full flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">4</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Family Protected</h3>
              <p className="text-gray-600 leading-relaxed">
                If something happens to you during the term, your beneficiaries receive the full death benefit tax-free to cover expenses.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Indiana-Specific Considerations */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#1A3A52] mb-6">
                Indiana-Specific Considerations
              </h2>
              <p className="text-lg text-[#4A5568] mb-8 leading-relaxed">
                Indiana residents benefit from specific state regulations and protections when purchasing term life insurance. Here's what you need to know:
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#D97642]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-government-line text-[#D97642] text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Indiana Department of Insurance Protection</h3>
                    <p className="text-gray-600 leading-relaxed">
                      All carriers are regulated by the Indiana Department of Insurance, ensuring fair practices and consumer protection. File complaints at <strong>in.gov/idoi</strong> if needed.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-shield-check-line text-[#1A3A52] text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Indiana Life &amp; Health Guaranty Association</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Your policy is protected up to <strong>$300,000</strong> by the state guaranty association if your carrier becomes insolvent—one of the highest limits in the nation.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#D97642]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-money-dollar-circle-line text-[#D97642] text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">No State Income Tax on Death Benefits</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Indiana does not tax life insurance death benefits, and there's no state estate tax. Your beneficiaries receive the full amount tax-free.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-time-line text-[#1A3A52] text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">30-Day Free Look Period</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Indiana law requires a minimum 10-day free look period, but most carriers offer 30 days. Cancel for any reason and get a full refund.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#D97642]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-map-pin-line text-[#D97642] text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Local Market Rates</h3>
                    <p className="text-gray-600 leading-relaxed">
                      <a href="/service-areas/indianapolis" className="text-[#D97642] font-semibold hover:underline cursor-pointer">Indianapolis residents</a> typically pay <strong>8-12% less</strong> than the national average due to Indiana's lower cost of living and competitive insurance market. Families in <a href="/service-areas/carmel" className="text-[#D97642] font-semibold hover:underline cursor-pointer">Carmel</a>, <a href="/service-areas/fishers" className="text-[#D97642] font-semibold hover:underline cursor-pointer">Fishers</a>, and surrounding areas also benefit from these competitive rates.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Indiana%20state%20capitol%20building%20in%20Indianapolis%20with%20clear%20blue%20sky%20daytime%20professional%20architectural%20photography%20wide%20angle%20shot%20showing%20the%20full%20dome%20and%20surrounding%20grounds%20representing%20government%20regulation%20and%20consumer%20protection%20clean%20modern%20composition&width=600&height=700&seq=termlife-capitol-2025b&orientation=portrait"
                alt="Indiana State Capitol representing consumer protection"
                title="Indiana State Capitol — Consumer Protections for Life Insurance Policyholders in Indiana"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
                className="rounded-2xl shadow-2xl w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Term Life vs Whole Life Calculator - REPLACED WITH INTERACTIVE CALCULATOR */}
      <PremiumCalculator />

      {/* Who Needs Term Life Insurance */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1A3A52] mb-4">
              Who Needs Term Life Insurance?
            </h2>
            <p className="text-xl text-[#4A5568] max-w-3xl mx-auto">
              If people depend on your paycheck, you need term life insurance. Here are the four groups we hear from most in Indianapolis.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-br from-[#D97642] to-[#C26638] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-parent-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Young Families</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Protect your spouse and children if you're the primary breadwinner. Cover mortgage, childcare, and college expenses.
              </p>
              <div className="text-sm text-[#D97642] font-semibold">
                Recommended: $500K-$1M
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">New Homeowners</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Ensure your mortgage is paid off if something happens. Protect your family from losing their home. Learn more about <a href="/mortgage-protection" className="text-[#D97642] font-semibold hover:underline cursor-pointer">mortgage protection insurance</a>.
              </p>
              <div className="text-sm text-[#D97642] font-semibold">
                Recommended: Match mortgage balance
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-br from-[#D97642] to-[#C26638] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-briefcase-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Self-Employed</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                No employer-provided life insurance? Protect your business and family income with affordable term coverage. Consider <a href="/whole-life" className="text-[#D97642] font-semibold hover:underline cursor-pointer">whole life insurance</a> for permanent protection.
              </p>
              <div className="text-sm text-[#D97642] font-semibold">
                Recommended: 10x annual income
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Single Parents</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Ensure your children are financially secure and can continue their education if you're not there.
              </p>
              <div className="text-sm text-[#D97642] font-semibold">
                Recommended: $750K-$1M
              </div>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-[#1A3A52] to-[#0F2A3F] rounded-xl p-8 text-white">
            <div className="flex items-start gap-4">
              <i className="ri-calculator-line text-3xl flex-shrink-0 mt-1"></i>
              <div>
                <h4 className="text-xl font-bold mb-3">Quick Coverage Calculator</h4>
                <p className="text-white/80 leading-relaxed mb-4">
                  A common rule of thumb: <strong className="text-white">10-12x your annual income</strong> plus outstanding debts (mortgage, car loans, credit cards). For example, if you earn $75,000/year with a $200,000 mortgage, consider $950,000-$1,100,000 in coverage.
                </p>
                <button
                  onClick={() => setShowBooking(true)}
                  className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer"
                >
                  Get Personalized Recommendation
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Apply in Indianapolis */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="apply-section-heading" className="text-4xl font-bold text-gray-900 mb-4">
              How to Apply for Term Life in Indianapolis
            </h2>
            <p className="text-xl text-gray-600">
              Get covered in as little as 24 hours with our streamlined process
            </p>
          </div>

          <div className="space-y-8">
            <div id="apply-step-1" className="flex gap-6 items-start">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-[#D97642] to-[#C26638] rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
              </div>
              <div className="flex-1 bg-gradient-to-br from-white to-white rounded-xl p-8 border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Get Your Free Quote (5 Minutes)</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Answer a few simple questions about your age, health, and coverage needs. We'll instantly compare rates from 20+ top-rated carriers including Mutual of Omaha, Prudential, and Nationwide.
                </p>
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#D97642]"></i>
                    <span>No personal info required</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#D97642]"></i>
                    <span>Instant results</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#D97642]"></i>
                    <span>No obligation</span>
                  </div>
                </div>
              </div>
            </div>

            <div id="apply-step-2" className="flex gap-6 items-start">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
              </div>
              <div className="flex-1 bg-gradient-to-br from-white to-white rounded-xl p-8 border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Complete Your Application (15 Minutes)</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Once you've chosen your policy, complete a simple online application or schedule a call with one of our licensed Indianapolis agents. Most applicants qualify for <strong>no medical exam</strong> coverage.
                </p>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-600 mb-2"><strong className="text-gray-900">What you'll need:</strong></p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Basic health information (height, weight, medications)</li>
                    <li>• Beneficiary details (who receives the death benefit)</li>
                    <li>• Driver's license or state ID</li>
                  </ul>
                </div>
              </div>
            </div>

            <div id="apply-step-3" className="flex gap-6 items-start">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-[#D97642] to-[#C26638] rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
              </div>
              <div className="flex-1 bg-gradient-to-br from-white to-white rounded-xl p-8 border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Get Approved &amp; Activate Coverage</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Most Indiana residents receive approval within <strong>24-48 hours</strong>. Once approved, your coverage begins immediately after your first payment. You'll receive your policy documents via email and mail.
                </p>
                <div className="flex items-start gap-3 bg-[#D97642]/10 rounded-lg p-4">
                  <i className="ri-shield-check-line text-[#D97642] text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-[#1A3A52]">
                    <strong>30-Day Money-Back Guarantee:</strong> Review your policy risk-free. If you're not satisfied for any reason, cancel within 30 days for a full refund.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => setShowBooking(true)}
              className="px-10 py-5 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all shadow-lg hover:shadow-xl text-lg whitespace-nowrap cursor-pointer"
            >
              Start Your Application Now
            </button>
            <p className="mt-4 text-sm text-gray-500">
              Or call us at <a href="tel:+13176482983" className="text-[#D97642] font-semibold hover:underline cursor-pointer">(317) 648-2983</a> to speak with a licensed agent
            </p>
          </div>
        </div>
      </section>

      {/* Meet Our Licensed Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20team%20of%20life%20insurance%20specialists%20in%20a%20modern%20office%20setting%20three%20diverse%20agents%20smiling%20confidently%20dressed%20in%20business%20attire%20warm%20natural%20light%20clean%20modern%20workspace%20representing%20licensed%20Indiana%20financial%20professionals%20trustworthy%20team%20environment&width=600&height=700&seq=team-hoosier-life-2025a&orientation=portrait"
                alt="Licensed Life Insurance Specialists at Hoosier Life Insurance — Serving All of Indiana"
                title="Licensed Life Insurance Specialists at Hoosier Life Insurance — Indianapolis, Indiana"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
                className="rounded-2xl shadow-2xl w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-6 right-6 bg-white rounded-xl px-6 py-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#D97642]/10 rounded-full flex items-center justify-center">
                    <i className="ri-shield-star-line text-[#D97642] text-2xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Licensed in Indiana</div>
                    <div className="text-lg font-bold text-[#1A3A52]">20+ A-Rated Carriers</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-group-line"></i>
                Your Indiana Life Insurance Team
              </div>
              <h2 className="text-4xl font-bold text-[#1A3A52] mb-6">
                Meet Our Licensed Life Insurance Specialists
              </h2>
              <p className="text-lg text-[#4A5568] mb-6 leading-relaxed">
                Hoosier Life Insurance is a team of licensed Indiana life insurance specialists — not a single agent, but a dedicated group of professionals ready to serve you by phone or virtual consultation, anywhere in Indiana.
              </p>
              <p className="text-lg text-[#4A5568] mb-8 leading-relaxed">
                We work with 20+ A-rated carriers to find you the best rates — not just the easiest sale. Whether you're a young family in Fishers, a new homeowner in Carmel, or planning for retirement in Noblesville, we'll walk you through your options with zero pressure and complete transparency.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D97642]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-[#D97642] text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Serving All of Indiana by Phone & Video</h3>
                    <p className="text-gray-600">No office visit required. We serve every Indiana family — from Indianapolis to Fort Wayne, Bloomington to South Bend.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-star-line text-[#1A3A52] text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Independent — Not Tied to Any One Company</h3>
                    <p className="text-gray-600">We compare rates from Mutual of Omaha, Prudential, Nationwide, North American, Transamerica, and 15+ more to find your best option.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D97642]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-hand-heart-line text-[#D97642] text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">No-Pressure, Honest Approach</h3>
                    <p className="text-gray-600">Our job is to educate and guide — never to pressure. You make the final decision on your own terms.</p>
                  </div>
                </div>
              </div>

              {/* Author bio block */}
              <div className="bg-[#F9F7F4] rounded-xl p-5 border border-gray-200 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
                    <i className="ri-group-line text-white text-lg"></i>
                  </div>
                  <div>
                    <div className="font-bold text-[#1A3A52] text-sm">Written by the Licensed Life Insurance Specialists at Hoosier Life Insurance</div>
                    <div className="text-xs text-gray-500 mt-0.5">Licensed in Indiana · Independent — 20+ Carriers · Last reviewed March 2025</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowBooking(true)}
                  className="px-8 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Schedule a Consultation
                </button>
                <a
                  href="tel:+13176482983"
                  className="px-8 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg font-semibold hover:border-[#D97642] hover:text-[#D97642] transition-all whitespace-nowrap cursor-pointer"
                >
                  Call (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles & Internal Links */}
      <section className="py-16 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D97642]/10 rounded-full mb-4">
              <i className="ri-article-line text-[#D97642]"></i>
              <span className="text-sm font-medium text-[#D97642]">Continue Learning</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Related Articles &amp; Resources</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Deepen your understanding of term life insurance with these related guides from our Indiana specialists.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Blog: Term vs Whole Life */}
            <a href="/blog/term-vs-whole-life-indianapolis" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                  <i className="ri-scales-3-line text-xl text-[#D97642] group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#D97642] uppercase tracking-wider">Blog</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Term vs. Whole Life Insurance in Indianapolis</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Which policy type builds more wealth for your family? Side-by-side analysis with real Indianapolis numbers.</p>
                </div>
              </div>
            </a>

            {/* Blog: Life Insurance Rates */}
            <a href="/blog/life-insurance-rates-indianapolis" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                  <i className="ri-bar-chart-2-line text-xl text-[#D97642] group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#D97642] uppercase tracking-wider">Blog</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Life Insurance Rates in Indianapolis: What to Expect</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Real 2025 rate tables for Indianapolis residents by age, health class, and coverage amount.</p>
                </div>
              </div>
            </a>

            {/* Blog: How Much Coverage */}
            <a href="/blog/how-much-life-insurance-indiana" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                  <i className="ri-calculator-line text-xl text-[#D97642] group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#D97642] uppercase tracking-wider">Blog</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">How Much Life Insurance Does Your Indiana Family Need?</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Use the DIME method to calculate exact coverage—no guesswork, no rule-of-thumb shortcuts.</p>
                </div>
              </div>
            </a>

            {/* Resource: Term vs Whole for Homeowners */}
            <a href="/resources/term-vs-whole-life-indianapolis-homeowners" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0 group-hover:bg-[#1A3A52] transition-colors">
                  <i className="ri-home-4-line text-xl text-[#1A3A52] group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#1A3A52] uppercase tracking-wider">Resource Guide</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Term vs. Whole Life for Indianapolis Homeowners</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Which policy type is right for your mortgage? A complete homeowner's decision framework.</p>
                </div>
              </div>
            </a>

            {/* Resource: How Much Coverage */}
            <a href="/resources/how-much-life-insurance-indiana-families-need" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0 group-hover:bg-[#1A3A52] transition-colors">
                  <i className="ri-parent-line text-xl text-[#1A3A52] group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#1A3A52] uppercase tracking-wider">Resource Guide</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">How Much Coverage Indiana Families Really Need</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Step-by-step DIME calculation with Indiana-specific cost-of-living data and college costs.</p>
                </div>
              </div>
            </a>

            {/* Blog: Life Insurance Mistakes */}
            <a href="/resources/life-insurance-mistakes-indiana-families" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0 group-hover:bg-[#1A3A52] transition-colors">
                  <i className="ri-alert-line text-xl text-[#1A3A52] group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#1A3A52] uppercase tracking-wider">Blog</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">7 Life Insurance Mistakes Indianapolis Families Make</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Avoid the coverage errors that leave Indiana families underinsured or overpaying every month.</p>
                </div>
              </div>
            </a>
          </div>

          <div className="mt-10 bg-gradient-to-r from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-3">Ready to Get Your Term Life Quote?</h3>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">Our licensed Indiana agents compare 20+ carriers to find your best rate. Free, no-obligation quote in minutes.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleCTAClick}
                className="px-8 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap"
              >
                Get My Free Quote
              </button>
              <a href="tel:+13176482983" className="px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap inline-flex items-center justify-center gap-2">
                <i className="ri-phone-line"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <MobileProductCTABar productName="Term Life Insurance" quoteHref="/instant-quote" />

      {/* Booking Modal */}
      {showBooking && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900">Schedule Your Free Consultation</h3>
              <button
                onClick={() => setShowBooking(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>
            <div className="p-6">
              <BookingCalendar
                timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                texts={{
                  heading: 'Book a Consultation',
                  step0Title: 'Select a Date',
                  step1Title: 'Select a Time',
                  step2Title: 'Review & Book',
                  selectDate: 'Select Date',
                  selectTime: 'Select Time',
                  reviewAndBook: 'Review & Book',
                  previousMonth: 'Previous Month',
                  nextMonth: 'Next Month',
                  noSlotsAvailable: 'No slots available',
                  selectedDateTime: 'Selected Date & Time',
                  modifyTime: 'Modify',
                  customerName: 'Your Name',
                  customerPhone: 'Phone Number',
                  notes: 'Notes',
                  submitButton: 'Confirm Booking',
                  submitting: 'Submitting...',
                  successMessage: 'Booking confirmed! We\'ll be in touch shortly.',
                  errorMessage: 'Something went wrong. Please try again.',
                  backButton: 'Back',
                  required: '*',
                }}
                onSubmitSuccess={() => {
                  trackConversion({ action: 'booking_submitted', page: 'term-life' });
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}